/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import arClient from '../../util/api_client';
import { isApiSuccess } from '../../constants/platformStatusCodes';
import {
  buildAudienceCreatePayload,
  dispatchQuotaExceededIfNeeded,
  isCreateAudienceSuccess,
  NON_TERMINAL_AUDIENCE_STATUSES,
  normalizeAudience,
  normalizeAudienceSegment,
  normalizeCreatedAudienceCard,
  normalizeAudiencesCampaign,
  parseScoreBreakdown,
} from './utils/audienceUtils';

// Re-export pure helpers consumed directly by components / other slices.
export {
  AUDIENCE_STATUS,
  isProcessingStatus,
  isCompletedStatus,
  normalizeAudienceSegment,
  NON_TERMINAL_AUDIENCE_STATUSES,
} from './utils/audienceUtils';

// ─── Polling timer registry (module-level, not Redux state) ──────────────────

const audiencePollingTimers = new Map();

// ─── Slice ────────────────────────────────────────────────────────────────────

const initialState = {
  audienceSegments: [],
  audienceSegmentsLoading: false,
  audienceSegmentsError: null,
  totalSegments: 0,
  segmentsLoading: false,
  audienceProgress: {},
  generateMoreProgress: {},
  drawerCampaigns: [],
  drawerCampaignsLoading: false,
  drawerCampaignsError: null,
  segmentDetail: null,
  segmentDetailLoading: false,
  influencerReport: {},
  influencerReportLoading: false,
  searchParams: {
    url: '',
    promoteCategory: '',
    promoteCustom: '',
    targetDescription: '',
    segmentation: {
      language: null,
      age: null,
      incomeLevel: null,
      education: null,
      ethnicity: null,
      gender: null,
      location: null,
    },
  },
};

const AudiencesSlice = createSlice({
  name: 'Audiences',
  initialState,
  reducers: {
    setAudienceSegments(state, action) {
      state.audienceSegments = action.payload;
    },
    setAudienceSegmentsLoading(state, action) {
      state.audienceSegmentsLoading = action.payload;
    },
    setAudienceSegmentsError(state, action) {
      state.audienceSegmentsError = action.payload;
    },
    setTotalSegments(state, action) {
      state.totalSegments = action.payload;
    },
    setSegmentsLoading(state, action) {
      state.segmentsLoading = action.payload;
    },
    setAudienceProgress(state, action) {
      const { id, percent } = action.payload;
      state.audienceProgress[id] = percent;
    },
    clearAudienceProgress(state, action) {
      delete state.audienceProgress[action.payload];
    },
    setGenerateMoreProgress(state, action) {
      const { id, percent } = action.payload;
      state.generateMoreProgress[id] = percent;
    },
    clearGenerateMoreProgress(state, action) {
      delete state.generateMoreProgress[action.payload];
    },
    setDrawerCampaigns(state, action) {
      state.drawerCampaigns = action.payload;
    },
    setDrawerCampaignsLoading(state, action) {
      state.drawerCampaignsLoading = action.payload;
    },
    setDrawerCampaignsError(state, action) {
      state.drawerCampaignsError = action.payload;
    },
    setSearchParams(state, action) {
      state.searchParams = { ...state.searchParams, ...action.payload };
    },
    setSegmentationFilter(state, action) {
      const { key, value } = action.payload;
      state.searchParams.segmentation[key] = value;
    },
    resetSearchParams(state) {
      state.searchParams = initialState.searchParams;
    },
    upsertAudience(state, action) {
      const audience = action.payload;
      const index = state.audienceSegments.findIndex((item) => item.id === audience.id);
      if (index >= 0) {
        state.audienceSegments[index] = { ...state.audienceSegments[index], ...audience };
      } else {
        state.audienceSegments.unshift(audience);
      }
    },
    setSegmentDetail(state, action) {
      state.segmentDetail = action.payload;
    },
    setSegmentDetailLoading(state, action) {
      state.segmentDetailLoading = action.payload;
    },
    setInfluencerReport(state, action) {
      state.influencerReport = action.payload;
    },
    setInfluencerReportLoading(state, action) {
      state.influencerReportLoading = action.payload;
    },
  },
});

export const {
  setAudienceSegments,
  setAudienceSegmentsLoading,
  setAudienceSegmentsError,
  setTotalSegments,
  setSegmentsLoading,
  setAudienceProgress,
  clearAudienceProgress,
  setGenerateMoreProgress,
  clearGenerateMoreProgress,
  setDrawerCampaigns,
  setDrawerCampaignsLoading,
  setDrawerCampaignsError,
  setSearchParams,
  setSegmentationFilter,
  resetSearchParams,
  upsertAudience,
  setSegmentDetail,
  setSegmentDetailLoading,
  setInfluencerReport,
  setInfluencerReportLoading,
} = AudiencesSlice.actions;

/**
 * GET /api/v1/audiences
 */
export const fetchAudiences = (params = {}) => (dispatch) => {
  dispatch(setAudienceSegmentsLoading(true));
  dispatch(setAudienceSegmentsError(null));

  const query = new URLSearchParams();
  if (params.search?.trim()) query.set('search', params.search.trim());
  if (params.status === 'archived') query.set('status', 'archived');
  const qs = query.toString();
  const endpoint = `/audiences${qs ? `?${qs}` : ''}`;

  arClient.doApiCallGet(endpoint, (resp) => {
    if (isApiSuccess(resp)) {
      const list = Array.isArray(resp.data) ? resp.data : [];
      dispatch(setAudienceSegments(list.map(normalizeAudience)));
      dispatch(setTotalSegments(resp.totalSegments ?? 0));
    } else {
      dispatch(setAudienceSegmentsError(resp?.message ?? 'Failed to load audiences.'));
    }
    dispatch(setAudienceSegmentsLoading(false));
  });
};

// Keep legacy export name as an alias so nothing else breaks during migration.
export const searchAudienceSegments = fetchAudiences;

export const stopAudiencePolling = (audienceId) => {
  const key = String(audienceId);
  const timer = audiencePollingTimers.get(key);
  if (timer) {
    clearInterval(timer);
    audiencePollingTimers.delete(key);
  }
};

/**
 * GET /api/v1/audiences/{audienceId}/segments — snapshot for cards + polling fallback.
 *   silent: true skips the segmentsLoading flag (used by polling to avoid loader flashing)
 */
export const fetchAudienceSegments = (audienceId, { silent = false } = {}) => (
  dispatch,
  getState
) => {
  const id = String(audienceId);
  if (!id) return Promise.resolve();

  if (!silent) dispatch(setSegmentsLoading(true));

  return new Promise((resolve) => {
    arClient.doApiCallGet(`/audiences/${id}/segments`, (resp) => {
      if (isApiSuccess(resp) && resp.data) {
        const { status, segments = [], campaignId, limits, influencersPending } = resp.data;
        const existing = getState().Audiences.audienceSegments.find((a) => a.id === id);
        const normalized = segments.map(normalizeAudienceSegment);
        const activeCount = normalized.filter(
          (s) => !s.isArchived && s.enrichmentStatus !== 'cancelled'
        ).length;
        dispatch(
          upsertAudience({
            ...(existing ?? { id }),
            status: status ?? existing?.status,
            audienceCount: activeCount,
            segments: normalized,
            // True only while a campaign copy is still receiving its influencer list
            // (FE_INFLUENCER_GENERATION_STATUS_GUIDE §2). Explicit false when absent — never
            // inherit from `existing`; a stale true would keep the influencers poll alive forever.
            influencersPending: influencersPending === true,
            feedbackRuns: limits?.feedbackRuns ?? existing?.feedbackRuns ?? null,
          })
        );

        if (!NON_TERMINAL_AUDIENCE_STATUSES.includes(status)) {
          stopAudiencePolling(id);
        }
        if (campaignId != null) {
          stopAudiencePolling(id);
        }
      }
      if (!silent) dispatch(setSegmentsLoading(false));
      resolve(resp);
    });
  });
};

/**
 * GET /api/v1/audiences/{audienceId}/segments/{segmentId}
 * Refreshes ONE segment card in Redux state (used by .segment.ready / .segment.image.ready
 * socket events — guide §5). Avoids re-fetching the whole list.
 */
export const fetchSingleSegmentCard = (audienceId, segmentId) => (dispatch, getState) => {
  const aId = String(audienceId);
  const sId = String(segmentId);

  return new Promise((resolve) => {
    arClient.doApiCallGet(`/audiences/${aId}/segments/${sId}`, (resp) => {
      if (isApiSuccess(resp) && resp.data?.segment) {
        const updated = normalizeAudienceSegment(resp.data.segment);
        const existing = getState().Audiences.audienceSegments.find((a) => a.id === aId);
        if (existing) {
          const segments = (existing.segments ?? []).map((s) =>
            s.id === updated.id
              ? // Preserve influencersStatus when the single-segment payload omits it — a null
                // here would drop the "generating" spinner mid-generation on .segment.ready /
                // .segment.image.ready refreshes.
                { ...updated, influencersStatus: updated.influencersStatus ?? s.influencersStatus }
              : s
          );
          dispatch(upsertAudience({ ...existing, segments }));
        }
      }
      resolve(resp);
    });
  });
};

/**
 * GET /api/v1/audiences/{audienceId}/segments/{segmentId}/influencers
 * Read-only per-segment influencer list (FE_SEGMENT_INFLUENCERS_GUIDE §4). Generation is
 * automatic — the FE never triggers it. Returns the raw resp; callers normalise it with
 * `normalizeSegmentInfluencersResponse`.
 */
export const fetchSegmentInfluencers = (audienceId, segmentId, { limit, offset } = {}) => () => {
  const aId = String(audienceId);
  const sId = String(segmentId);
  const query = new URLSearchParams();
  if (limit != null) query.set('limit', String(limit));
  if (offset != null) query.set('offset', String(offset));
  const qs = query.toString();
  const endpoint = `/audiences/${aId}/segments/${sId}/influencers${qs ? `?${qs}` : ''}`;

  return new Promise((resolve) => {
    arClient.doApiCallGet(endpoint, (resp) => resolve(resp));
  });
};

/**
 * POST /api/v1/audiences/{audienceId}/segments/{segmentId}/influencers/archive
 * Archives (`archived: true`) or unarchives (`archived: false`) a single influencer in a
 * segment (FE_ARCHIVE_UNARCHIVE §2). Idempotent toggle-by-value — the same endpoint does both.
 * Rows are identified by `username` (the row's `handle`) + `platform`. Resolves the raw resp;
 * `resp.data.archived` is the resulting state.
 */
export const setSegmentInfluencerArchived = (
  audienceId,
  segmentId,
  { username, platform, archived }
) => () => {
  const aId = String(audienceId);
  const sId = String(segmentId);
  return new Promise((resolve) => {
    arClient.doApiCallV2(
      `/audiences/${aId}/segments/${sId}/influencers/archive`,
      { username, platform, archived },
      (resp) => resolve(resp)
    );
  });
};

/**
 * GET /api/v1/audiences/{audienceId}/segments/{segmentId}/influencers/{platform}/{username}
 * Full detail for one influencer — identity + score + the normalized creator report
 * (FE_INFLUENCER_DETAIL_ENDPOINT). `data.report` already arrives decoded (no JSON.parse). The
 * report is stored flat (with `rationale` attached) so the shared creator-report components render
 * it, mirroring the Universal Discovery report flow. Null / failed / 404 → {}.
 */
export const getAudienceInfluencerReport = (audienceId, segmentId, platform, username) => (
  dispatch
) => {
  const aId = String(audienceId);
  const sId = String(segmentId);
  const endpoint = `/audiences/${aId}/segments/${sId}/influencers/${platform}/${encodeURIComponent(
    username
  )}`;

  dispatch(setInfluencerReport({}));
  dispatch(setInfluencerReportLoading(true));

  return new Promise((resolve) => {
    arClient.doApiCallGet(endpoint, (resp) => {
      const report = resp?.data?.report;
      if (isApiSuccess(resp) && report && report.reportState !== 'failed') {
        // Some report generations send `brandAffinity` / `interests` as string[], but the shared
        // creator-report components expect `[{ name }]` (e.g. CreatorDetailsHashtagBrands does
        // `item.name.replace(...)`, which throws on a bare string and, via the app error boundary,
        // remounts the whole app in a fetch loop). Coerce defensively to the expected shape.
        const toNamed = (arr) =>
          Array.isArray(arr)
            ? arr
                .map((item) => (typeof item === 'string' ? { name: item } : item))
                .filter((item) => item && item.name)
            : arr;
        // The score lives next to the report (the influencer row), not inside it, so it has to
        // be lifted onto the flat report object the card reads — otherwise the card gets no
        // score and renders an empty value.
        const influencer = resp.data.influencer ?? resp.data.agInfluencer ?? resp.data;
        const score =
          influencer.score ?? influencer.radScore ?? report.score ?? report.radScore ?? null;
        dispatch(
          setInfluencerReport({
            ...report,
            rationale: resp.data.rationale ?? report.rationale ?? null,
            brandAffinity: toNamed(report.brandAffinity),
            interests: toNamed(report.interests),
            score,
            scoreBreakdown: parseScoreBreakdown(influencer.scoreBreakdown ?? report.scoreBreakdown),
          })
        );
      } else {
        dispatch(setInfluencerReport({}));
      }
      dispatch(setInfluencerReportLoading(false));
      resolve(resp);
    });
  });
};

/**
 * GET /api/v1/audiences/{audienceId}/segments/{segmentId}
 * Loads the full segment into state.Audiences.segmentDetail for AudienceResult to render.
 * Fully decoupled from Personas state to avoid side effects on other Persona pages.
 */
export const fetchSegmentDetail = (audienceId, segmentId) => (dispatch) => {
  const aId = String(audienceId);
  const sId = String(segmentId);

  dispatch(setSegmentDetail(null));
  dispatch(setSegmentDetailLoading(true));

  return new Promise((resolve) => {
    arClient.doApiCallGet(`/audiences/${aId}/segments/${sId}`, (resp) => {
      if (isApiSuccess(resp) && resp.data?.segment) {
        dispatch(setSegmentDetail(resp.data.segment));
      }
      dispatch(setSegmentDetailLoading(false));
      resolve(resp);
    });
  });
};

export const startAudiencePolling = (audienceId) => (dispatch) => {
  const key = String(audienceId);
  stopAudiencePolling(audienceId);

  const poll = () => dispatch(fetchAudienceSegments(audienceId, { silent: true }));
  poll();
  audiencePollingTimers.set(key, setInterval(poll, 4000));
};

/**
 * PATCH /api/v1/audiences/{audienceId}/campaign
 * Moves the whole audience into a campaign.
 * @param {object} [options]
 * @param {boolean} [options.markNeutralLiked=false] When true, marks every non-disliked segment as liked.
 */
export const addAudienceToCampaign = (
  audienceId,
  campaignId,
  { markNeutralLiked = false } = {}
) => () => {
  const id = String(audienceId);
  const body = { campaignId };
  if (markNeutralLiked) {
    body.markNeutralLiked = true;
  }
  return new Promise((resolve) => {
    arClient.doApiCallV2(`/audiences/${id}/campaign`, body, (resp) => resolve(resp), {
      method: 'PATCH',
    });
  });
};

/**
 * POST /api/v1/audiences — create audience (async pipeline; poll segments after).
 */
export const createAudience = () => (dispatch, getState) => {
  const { searchParams } = getState().Audiences;
  if (!searchParams.url?.trim()) return Promise.resolve();

  dispatch(setAudienceSegmentsLoading(true));
  dispatch(setAudienceSegmentsError(null));

  const payload = buildAudienceCreatePayload(searchParams);

  return new Promise((resolve) => {
    arClient.doApiCallV2('/audiences', payload, (resp) => {
      dispatch(setAudienceSegmentsLoading(false));

      if (isCreateAudienceSuccess(resp) && resp.data) {
        const card = normalizeCreatedAudienceCard(resp.data, searchParams);
        dispatch(upsertAudience(card));
        resolve(resp);
        return;
      }

      dispatchQuotaExceededIfNeeded(dispatch, resp);
      resolve(resp);
    });
  });
};

/**
 * GET /api/v1/audiences/{id}/segments?status=archived
 * Returns the archived segments without writing to Redux (component-local use).
 */
export const fetchArchivedAudienceSegments = (audienceId) => () => {
  const id = String(audienceId);
  return new Promise((resolve) => {
    arClient.doApiCallGet(`/audiences/${id}/segments?status=archived`, (resp) => {
      if (isApiSuccess(resp) && resp.data) {
        resolve((resp.data.segments ?? []).map(normalizeAudienceSegment));
      } else {
        resolve([]);
      }
    });
  });
};

/**
 * PATCH /api/v1/audiences/{audienceId}/segments/selection
 * Saves Like / Dislike feedback. Both arrays are integers (segmentId values).
 * Either may be omitted; only the ids you send are changed.
 */
export const saveSegmentSelection = (
  audienceId,
  likedSegmentIds = [],
  dislikedSegmentIds = []
) => () => {
  const id = String(audienceId);
  const body = {};
  if (likedSegmentIds.length) body.likedSegmentIds = likedSegmentIds.map(Number);
  if (dislikedSegmentIds.length) body.dislikedSegmentIds = dislikedSegmentIds.map(Number);
  return new Promise((resolve) => {
    arClient.doApiCallV2(`/audiences/${id}/segments/selection`, body, (resp) => resolve(resp), {
      method: 'PATCH',
    });
  });
};

/**
 * PATCH /api/v1/audiences/{audienceId}/segments/{segmentId}
 * Archives a segment by setting archiveStatus to "Archived" (guide §4.9).
 */
export const archiveSegment = (audienceId, segmentId) => () => {
  const aId = String(audienceId);
  const sId = String(segmentId);
  return new Promise((resolve) => {
    arClient.doApiCallV2(
      `/audiences/${aId}/segments/${sId}`,
      { archiveStatus: 'Archived' },
      (resp) => resolve(resp),
      { method: 'PATCH' }
    );
  });
};

/**
 * PATCH /api/v1/audiences/{audienceId}/segments/{segmentId}
 * Restores an archived segment back to the Active tab (guide §4.9).
 */
export const unarchiveSegment = (audienceId, segmentId) => () => {
  const aId = String(audienceId);
  const sId = String(segmentId);
  return new Promise((resolve) => {
    arClient.doApiCallV2(
      `/audiences/${aId}/segments/${sId}`,
      { archiveStatus: 'Active' },
      (resp) => resolve(resp),
      { method: 'PATCH' }
    );
  });
};

/**
 * POST /api/v1/audiences/{audienceId}/segments/generate-more
 * Chat-style generate more segments with user feedback.
 */
export const generateMoreSegments = (
  audienceId,
  feedback,
  likedSegmentIds = [],
  dislikedSegmentIds = []
) => () => {
  const id = String(audienceId);
  const body = { feedback };
  if (likedSegmentIds.length) body.likedSegmentIds = likedSegmentIds;
  if (dislikedSegmentIds.length) body.dislikedSegmentIds = dislikedSegmentIds;
  return new Promise((resolve) => {
    arClient.doApiCallV2(`/audiences/${id}/segments/generate-more`, body, (resp) => resolve(resp));
  });
};

/**
 * GET /api/v1/audiences/campaigns — campaigns for the Add-to-Campaign drawer.
 * When `audienceId` (the master) is supplied it is sent as a query param so the backend flags
 * each campaign with `alreadyAssigned` for that master (guide §4).
 */
export const fetchDrawerCampaigns = (audienceId) => (dispatch) => {
  dispatch(setDrawerCampaignsLoading(true));
  dispatch(setDrawerCampaignsError(null));
  dispatch(setDrawerCampaigns([]));

  const query = new URLSearchParams();
  if (audienceId != null && String(audienceId).trim()) {
    query.set('audienceId', String(audienceId));
  }
  const qs = query.toString();
  const endpoint = `/audiences/campaigns${qs ? `?${qs}` : ''}`;

  arClient.doApiCallGet(endpoint, (resp) => {
    if (isApiSuccess(resp)) {
      const list = Array.isArray(resp.data) ? resp.data : [];
      dispatch(setDrawerCampaigns(list.map(normalizeAudiencesCampaign)));
    } else {
      dispatch(
        setDrawerCampaignsError(resp?.error ?? resp?.message ?? 'Failed to load campaigns.')
      );
    }
    dispatch(setDrawerCampaignsLoading(false));
  });
};

export default AudiencesSlice.reducer;
